import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { timesheetsAPI, usersAPI, subcontractorInvoicesAPI, clientInvoicesAPI, customEntriesAPI, jobsAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { formatDate, formatDateRange } from '../utils/dateUtils';
import { Plus, Search, Filter, Loader2, DollarSign, CheckCircle2, CreditCard, FileText } from 'lucide-react';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { className: 'badge-draft', label: 'Draft' },
    submitted: { className: 'badge-pending', label: 'Pending' },
    approved: { className: 'badge-approved', label: 'Approved' },
    rejected: { className: 'badge-rejected', label: 'Rejected' },
  };

  const config = statusConfig[status] || statusConfig.draft;
  return <span className={config.className}>{config.label}</span>;
};

const PaymentBadge = ({ subStatus, clientStatus }) => {
  // Show both client invoice and subcontractor payment status
  const hasSub = !!subStatus;
  const hasClient = !!clientStatus;
  
  if (!hasSub && !hasClient) {
    return <span className="px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-500">Not Linked</span>;
  }
  
  return (
    <div className="flex flex-col gap-1">
      {hasClient && (
        <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 inline-flex ${
          clientStatus === 'paid' 
            ? 'bg-emerald-100 text-emerald-700' 
            : 'bg-blue-100 text-blue-700'
        }`}>
          <CreditCard className="w-3 h-3" /> Client {clientStatus === 'paid' ? 'Paid' : 'Inv'}
        </span>
      )}
      {hasSub && (
        <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 inline-flex ${
          subStatus === 'paid' 
            ? 'bg-emerald-100 text-emerald-700' 
            : 'bg-amber-100 text-amber-700'
        }`}>
          <DollarSign className="w-3 h-3" /> Sub {subStatus === 'paid' ? 'Paid' : 'Pend'}
        </span>
      )}
    </div>
  );
};

const TimesheetsPage = () => {
  const { isAdmin } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [customEntries, setCustomEntries] = useState([]);
  const [jobs, setJobs] = useState({});
  const [users, setUsers] = useState({});
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [subPaymentStatusMap, setSubPaymentStatusMap] = useState({});
  const [clientInvoiceStatusMap, setClientInvoiceStatusMap] = useState({});

  // Generate period options (current month + last 6 months)
  const getPeriodOptions = () => {
    const options = [{ value: 'all', label: 'All Time' }];
    const today = new Date();
    
    for (let i = 0; i < 6; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      options.push({
        value: `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: `${monthName} ${year}`
      });
    }
    return options;
  };

  const periodOptions = getPeriodOptions();

  useEffect(() => {
    fetchTimesheets();
  }, [isAdmin]);

  const fetchTimesheets = async () => {
    try {
      const response = isAdmin 
        ? await timesheetsAPI.getAll()
        : await timesheetsAPI.getMy();
      setTimesheets(response.data);
      
      // Fetch users for admin view to show resource names
      if (isAdmin) {
        const [usersRes, jobsRes, customRes] = await Promise.all([
          usersAPI.getAll(),
          jobsAPI.getAll(),
          customEntriesAPI.getAll()
        ]);
        
        const usersMap = {};
        const subcontractors = [];
        usersRes.data.forEach(user => {
          usersMap[user.id] = user;
          if (user.role === 'subcontractor') {
            subcontractors.push(user);
          }
        });
        setUsers(usersMap);
        // Sort subcontractors alphabetically
        subcontractors.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setUsersList(subcontractors);
        
        // Jobs map
        const jobsMap = {};
        jobsRes.data.forEach(job => {
          jobsMap[job.id] = job;
        });
        setJobs(jobsMap);
        
        // Custom entries
        setCustomEntries(customRes.data);
        
        // Fetch subcontractor payment statuses
        const subInvoicesRes = await subcontractorInvoicesAPI.getAll();
        const subStatusMap = {};
        subInvoicesRes.data.forEach(invoice => {
          (invoice.timesheet_ids || []).forEach(tsId => {
            subStatusMap[tsId] = {
              status: invoice.status,
              invoiceNumber: invoice.invoice_number,
              paidDate: invoice.paid_date
            };
          });
          // Also map custom entry IDs
          (invoice.custom_entry_ids || []).forEach(ceId => {
            subStatusMap[`ce_${ceId}`] = {
              status: invoice.status,
              invoiceNumber: invoice.invoice_number,
              paidDate: invoice.paid_date
            };
          });
        });
        setSubPaymentStatusMap(subStatusMap);
        
        // Fetch client invoice statuses
        const clientInvoicesRes = await clientInvoicesAPI.getAll();
        const clientStatusMap = {};
        clientInvoicesRes.data.forEach(invoice => {
          (invoice.timesheet_ids || []).forEach(tsId => {
            clientStatusMap[tsId] = {
              status: invoice.status,
              invoiceNumber: invoice.invoice_number,
              paidDate: invoice.paid_date
            };
          });
        });
        setClientInvoiceStatusMap(clientStatusMap);
      }
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get payment status for a timesheet
  const getPaymentStatus = (tsId) => {
    const subStatus = subPaymentStatusMap[tsId]?.status;
    const clientStatus = clientInvoiceStatusMap[tsId]?.status;
    
    if (!subStatus && !clientStatus) return 'not_linked';
    if (subStatus === 'paid' && clientStatus === 'paid') return 'fully_paid';
    if (subStatus === 'paid') return 'sub_paid';
    if (clientStatus === 'paid') return 'client_paid';
    if (subStatus || clientStatus) return 'linked';
    return 'not_linked';
  };

  // Apply all filters
  const getFilteredTimesheets = () => {
    let filtered = timesheets;
    
    // Status filter
    if (filter !== 'all') {
      filtered = filtered.filter(ts => ts.status === filter);
    }
    
    // Resource filter (admin only)
    if (isAdmin && resourceFilter !== 'all') {
      filtered = filtered.filter(ts => ts.user_id === resourceFilter);
    }
    
    // Period filter
    if (periodFilter !== 'all') {
      filtered = filtered.filter(ts => {
        const periodStart = ts.period_start;
        return periodStart && periodStart.startsWith(periodFilter);
      });
    }
    
    // Payment status filter (admin only, only for approved timesheets)
    if (isAdmin && paymentFilter !== 'all') {
      filtered = filtered.filter(ts => {
        // Only filter approved timesheets by payment status
        if (ts.status !== 'approved') {
          return paymentFilter === 'not_linked' || paymentFilter === 'not_linked_client' || paymentFilter === 'not_linked_sub';
        }
        
        const subStatus = subPaymentStatusMap[ts.id]?.status;
        const clientStatus = clientInvoiceStatusMap[ts.id]?.status;
        const paymentStatus = getPaymentStatus(ts.id);
        
        switch (paymentFilter) {
          case 'not_linked':
            return paymentStatus === 'not_linked';
          case 'not_linked_client':
            // Show timesheets not linked to a client invoice
            return !clientStatus;
          case 'not_linked_sub':
            // Show timesheets not linked to a subcontractor payment
            return !subStatus;
          case 'sub_paid':
            return paymentStatus === 'sub_paid' || paymentStatus === 'fully_paid';
          case 'client_paid':
            return paymentStatus === 'client_paid' || paymentStatus === 'fully_paid';
          case 'fully_paid':
            return paymentStatus === 'fully_paid';
          case 'linked':
            return paymentStatus !== 'not_linked';
          default:
            return true;
        }
      });
    }
    
    return filtered;
  };

  // Apply filters to custom entries
  const getFilteredCustomEntries = () => {
    if (!isAdmin) return [];
    
    let filtered = customEntries;
    
    // Custom entries don't have status, so skip status filter unless 'all'
    // But show them if filter is 'all' or 'approved' (since custom entries are like approved work)
    if (filter !== 'all' && filter !== 'approved') {
      return [];
    }
    
    // Resource filter
    if (resourceFilter !== 'all') {
      filtered = filtered.filter(ce => ce.user_id === resourceFilter);
    }
    
    // Period filter
    if (periodFilter !== 'all') {
      filtered = filtered.filter(ce => {
        const periodStart = ce.period_start;
        return periodStart && periodStart.startsWith(periodFilter);
      });
    }
    
    // Payment status filter for custom entries
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(ce => {
        const subStatus = subPaymentStatusMap[`ce_${ce.id}`]?.status;
        
        switch (paymentFilter) {
          case 'not_linked':
            return !subStatus;
          case 'not_linked_client':
            // Custom entries are not linked to client invoices, so show all
            return true;
          case 'not_linked_sub':
            // Show custom entries not linked to a subcontractor payment
            return !subStatus;
          case 'sub_paid':
            return subStatus === 'paid';
          case 'client_paid':
            // Custom entries don't have client invoices, so hide
            return false;
          case 'fully_paid':
            // Custom entries only have sub status, so fully paid = sub paid
            return subStatus === 'paid';
          case 'linked':
            return !!subStatus;
          default:
            return true;
        }
      });
    }
    
    return filtered;
  };

  const filteredTimesheets = getFilteredTimesheets();
  const filteredCustomEntries = getFilteredCustomEntries();
  const totalItems = filteredTimesheets.length + filteredCustomEntries.length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              {isAdmin ? 'All Timesheets' : 'My Timesheets'}
            </h1>
            <p className="mt-1 text-slate-500">
              {isAdmin ? 'View and manage all submitted timesheets' : 'Track your weekly timesheet submissions'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Link to="/timesheet/new" className="btn-primary flex items-center gap-2" data-testid="create-timesheet-btn">
              <Plus className="w-4 h-4" />
              New Timesheet
            </Link>
            {isAdmin && (
              <Link to="/custom-entry/new" className="btn-secondary flex items-center gap-2" data-testid="create-custom-entry-btn">
                <Plus className="w-4 h-4" />
                <DollarSign className="w-4 h-4 -ml-2" />
                New Custom Entry
              </Link>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="card-base">
          <div className="card-body space-y-4">
            {/* Status Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {['all', 'draft', 'submitted', 'approved', 'rejected'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    filter === status
                      ? 'bg-[#194eb2] text-white'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`filter-${status}`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-white/20 rounded">
                    {status === 'all' ? timesheets.length : timesheets.filter(t => t.status === status).length}
                  </span>
                </button>
              ))}
            </div>
            
            {/* Resource and Period Filters */}
            <div className="flex flex-wrap gap-4 pt-2 border-t border-slate-100">
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600">Resource:</label>
                  <select
                    value={resourceFilter}
                    onChange={(e) => setResourceFilter(e.target.value)}
                    className="input-base w-48"
                    data-testid="resource-filter"
                  >
                    <option value="all">All Resources</option>
                    {[...usersList].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-600">Period:</label>
                <select
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  className="input-base w-48"
                  data-testid="period-filter"
                >
                  {periodOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-slate-600">Payment:</label>
                  <select
                    value={paymentFilter}
                    onChange={(e) => setPaymentFilter(e.target.value)}
                    className="input-base w-48"
                    data-testid="payment-filter"
                  >
                    <option value="all">All Payments</option>
                    <option value="not_linked">Not Linked</option>
                    <option value="not_linked_client">Not Linked to Client</option>
                    <option value="not_linked_sub">Not Linked to Sub</option>
                    <option value="linked">Linked (Any)</option>
                    <option value="sub_paid">Sub Paid</option>
                    <option value="client_paid">Client Paid</option>
                    <option value="fully_paid">Fully Paid</option>
                  </select>
                </div>
              )}
              
              {(resourceFilter !== 'all' || periodFilter !== 'all' || paymentFilter !== 'all') && (
                <button
                  onClick={() => {
                    setResourceFilter('all');
                    setPeriodFilter('all');
                    setPaymentFilter('all');
                  }}
                  className="text-sm text-[#194eb2] hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
            
            {/* Results count */}
            <div className="text-sm text-slate-500">
              Showing {totalItems} items ({filteredTimesheets.length} timesheets{isAdmin && filteredCustomEntries.length > 0 ? `, ${filteredCustomEntries.length} custom entries` : ''})
            </div>
          </div>
        </div>

        {/* Timesheets Table */}
        <div className="card-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="table-header">
                <tr>
                  {isAdmin && <th className="px-6 py-3 text-left">Resource</th>}
                  <th className="px-6 py-3 text-left">Period</th>
                  <th className="px-6 py-3 text-left">Days</th>
                  <th className="px-6 py-3 text-left">Hours</th>
                  <th className="px-6 py-3 text-left">Status</th>
                  {isAdmin && <th className="px-6 py-3 text-left">Payment</th>}
                  <th className="px-6 py-3 text-left">Submitted</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {totalItems === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 8 : 6} className="px-6 py-12 text-center text-slate-500">
                      No timesheets found
                    </td>
                  </tr>
                ) : (
                  <>
                    {/* Regular Timesheets */}
                    {filteredTimesheets.map((ts) => (
                      <tr key={ts.id} className="table-row">
                        {isAdmin && (
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">
                            {users[ts.user_id]?.name || '-'}
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <span className="font-medium text-slate-900">
                            {formatDate(ts.period_start)}
                          </span>
                          <span className="text-slate-400 mx-2">→</span>
                          <span className="text-slate-600">{formatDate(ts.period_end)}</span>
                        </td>
                        <td className="px-6 py-4 font-mono text-sm text-slate-600">
                          {ts.total_days > 0 ? ts.total_days : '-'}
                        </td>
                        <td className="px-6 py-4 font-mono text-sm text-slate-600">
                          {ts.total_hours > 0 ? ts.total_hours : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={ts.status} />
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-4">
                            {ts.status === 'approved' ? (
                              <PaymentBadge 
                                subStatus={subPaymentStatusMap[ts.id]?.status} 
                                clientStatus={clientInvoiceStatusMap[ts.id]?.status}
                              />
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {ts.submitted_at 
                            ? formatDate(ts.submitted_at.split('T')[0])
                            : '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Link
                            to={`/timesheets/${ts.id}`}
                            className="text-sm text-[#194eb2] hover:underline font-medium"
                            data-testid={`view-timesheet-${ts.id}`}
                          >
                            {ts.status === 'draft' ? 'Edit' : 'View'}
                          </Link>
                        </td>
                      </tr>
                    ))}
                    
                    {/* Custom Entries (Admin only) */}
                    {isAdmin && filteredCustomEntries.map((ce) => (
                      <tr key={`ce-${ce.id}`} className="table-row bg-amber-50/30">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">
                          <div className="flex items-center gap-2">
                            {users[ce.user_id]?.name || '-'}
                            <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">
                              Custom
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-medium text-slate-900">
                            {formatDate(ce.period_start)}
                          </span>
                          <span className="text-slate-400 mx-2">→</span>
                          <span className="text-slate-600">{formatDate(ce.period_end)}</span>
                        </td>
                        <td className="px-6 py-4 font-mono text-sm text-slate-600">
                          -
                        </td>
                        <td className="px-6 py-4 font-mono text-sm text-slate-600">
                          -
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={ce.status || 'submitted'} />
                          <span className="ml-2 text-xs text-slate-500">${ce.total_value.toFixed(2)}</span>
                        </td>
                        <td className="px-6 py-4">
                          <PaymentBadge 
                            subStatus={subPaymentStatusMap[`ce_${ce.id}`]?.status} 
                            clientStatus={null}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {ce.created_at 
                            ? formatDate(ce.created_at.split('T')[0])
                            : '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Link
                            to={`/custom-entry/${ce.id}`}
                            className="text-sm text-amber-600 hover:underline font-medium"
                            data-testid={`view-custom-entry-${ce.id}`}
                          >
                            Edit
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TimesheetsPage;
